import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { SFIcon } from '@/components/ui/sf-icon';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { useSkeletonDelay } from '@/hooks/useSkeletonDelay';
import { cn } from '@/lib/utils';
import { sfIcons } from '@/lib/sf-icons';
import { semanticTokens } from '@packages/config/theme';
import { Megaphone } from 'lucide-react';

interface NotificationSettingsProps {
  userId: string;
  isAdmin?: boolean;
}

export function NotificationSettings({ userId, isAdmin = false }: NotificationSettingsProps) {
  const { preferences, isLoading, isSaving, updatePreferences } = useNotificationPreferences(userId);
  const showSkeleton = useSkeletonDelay(isLoading, {
    delay: 150,
    trackingContext: 'notification-settings',
  });

  if (showSkeleton) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-96" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="flex items-center justify-between">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-6 w-10" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className="min-h-[200px]" aria-busy="true" aria-live="polite" />;
  }

  if (!preferences) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Failed to load notification preferences</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className={cn(semanticTokens.typography.h2, "tracking-tight")}>Notification Settings</h2>
        <p className="text-muted-foreground">
          Manage how and when you receive notifications
        </p>
      </div>

      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle className={cn(semanticTokens.typography.h3, "flex items-center gap-2")}>
            <SFIcon icon={sfIcons.settings} className="h-5 w-5" />
            General Preferences
          </CardTitle>
          <CardDescription className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
            Control how you receive notifications across different channels
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SFIcon icon={sfIcons.mail} className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="email-enabled">Email Notifications</Label>
            </div>
            <Switch
              id="email-enabled"
              checked={preferences.email_enabled}
              onCheckedChange={(checked) => updatePreferences({ email_enabled: checked })}
              disabled={isSaving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SFIcon icon={sfIcons.notifications} className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="in-app-enabled">In-App Notifications</Label>
            </div>
            <Switch
              id="in-app-enabled"
              checked={preferences.in_app_enabled}
              onCheckedChange={(checked) => updatePreferences({ in_app_enabled: checked })}
              disabled={isSaving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SFIcon icon={sfIcons.mobile} className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="push-enabled">Push Notifications</Label>
            </div>
            <Switch
              id="push-enabled"
              checked={preferences.push_enabled}
              onCheckedChange={(checked) => updatePreferences({ push_enabled: checked })}
              disabled={isSaving}
            />
          </div>
        </CardContent>
      </Card>

      {/* User Notification Categories */}
      <Card>
        <CardHeader>
          <CardTitle className={semanticTokens.typography.h3}>Notification Types</CardTitle>
          <CardDescription className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
            Choose which types of notifications you want to receive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SFIcon icon={sfIcons.security} className="h-4 w-4 text-primary" />
              <div>
                <Label htmlFor="kyc-updates">KYC Updates</Label>
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Identity verification status changes</p>
              </div>
            </div>
            <Switch
              id="kyc-updates"
              checked={preferences.kyc_updates}
              onCheckedChange={(checked) => updatePreferences({ kyc_updates: checked })}
              disabled={isSaving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SFIcon icon={sfIcons.card} className={cn("h-4 w-4", semanticTokens.positive.accent)} />
              <div>
                <Label htmlFor="transaction-updates">Transaction Updates</Label>
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Crypto deposits and fiat transfers</p>
              </div>
            </div>
            <Switch
              id="transaction-updates"
              checked={preferences.transaction_updates}
              onCheckedChange={(checked) => updatePreferences({ transaction_updates: checked })}
              disabled={isSaving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SFIcon icon={sfIcons.security} className={cn("h-4 w-4", semanticTokens.danger.accent)} />
              <div>
                <Label htmlFor="security-alerts">Security Alerts</Label>
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Login attempts and suspicious activity</p>
              </div>
            </div>
            <Switch
              id="security-alerts"
              checked={preferences.security_alerts}
              onCheckedChange={(checked) => updatePreferences({ security_alerts: checked })}
              disabled={isSaving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SFIcon icon={sfIcons.settings} className={cn("h-4 w-4", semanticTokens.info.accent)} />
              <div>
                <Label htmlFor="account-updates">Account Updates</Label>
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Bank account and address changes</p>
              </div>
            </div>
            <Switch
              id="account-updates"
              checked={preferences.account_updates}
              onCheckedChange={(checked) => updatePreferences({ account_updates: checked })}
              disabled={isSaving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SFIcon icon={sfIcons.notifications} className={cn("h-4 w-4", semanticTokens.warning.accent)} />
              <div>
                <Label htmlFor="maintenance-notices">Maintenance Notices</Label>
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Scheduled maintenance and downtime</p>
              </div>
            </div>
            <Switch
              id="maintenance-notices"
              checked={preferences.maintenance_notices}
              onCheckedChange={(checked) => updatePreferences({ maintenance_notices: checked })}
              disabled={isSaving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Megaphone className={cn("h-4 w-4", semanticTokens.info.accent)} />
              <div>
                <Label htmlFor="marketing">Marketing Updates</Label>
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Product updates and promotions</p>
              </div>
            </div>
            <Switch
              id="marketing"
              checked={preferences.marketing}
              onCheckedChange={(checked) => updatePreferences({ marketing: checked })}
              disabled={isSaving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Admin Notifications (only shown for admin users) */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className={cn(semanticTokens.typography.h3, "flex items-center gap-2")}>
              <SFIcon icon={sfIcons.security} className="h-5 w-5" />
              Admin Notifications
            </CardTitle>
            <CardDescription className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
              Administrative alerts and business intelligence reports
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="system-alerts">System Alerts</Label>
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Security issues and system health</p>
              </div>
              <Switch
                id="system-alerts"
                checked={preferences.system_alerts}
                onCheckedChange={(checked) => updatePreferences({ system_alerts: checked })}
                disabled={isSaving}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="business-reports">Business Reports</Label>
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Daily and weekly analytics summaries</p>
              </div>
              <Switch
                id="business-reports"
                checked={preferences.business_reports}
                onCheckedChange={(checked) => updatePreferences({ business_reports: checked })}
                disabled={isSaving}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="operations-alerts">Operations Alerts</Label>
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Large transactions and manual reviews</p>
              </div>
              <Switch
                id="operations-alerts"
                checked={preferences.operations_alerts}
                onCheckedChange={(checked) => updatePreferences({ operations_alerts: checked })}
                disabled={isSaving}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delivery Settings */}
      <Card>
        <CardHeader>
          <CardTitle className={cn(semanticTokens.typography.h3, "flex items-center gap-2")}>
            <SFIcon icon={sfIcons.pending} className="h-5 w-5" />
            Delivery Settings
          </CardTitle>
          <CardDescription className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
            Control when and how often you receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="digest-frequency">Email Frequency</Label>
            <Select
              value={preferences.digest_frequency}
              onValueChange={(value: 'immediate' | 'hourly' | 'daily' | 'weekly') =>
                updatePreferences({ digest_frequency: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Immediate</SelectItem>
                <SelectItem value="hourly">Hourly Digest</SelectItem>
                <SelectItem value="daily">Daily Digest</SelectItem>
                <SelectItem value="weekly">Weekly Summary</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="quiet-start">Quiet Hours Start</Label>
              <Input
                id="quiet-start"
                type="time"
                value={preferences.quiet_hours_start}
                onChange={(e) => updatePreferences({ quiet_hours_start: e.target.value })}
                disabled={isSaving}
              />
            </div>

            <div>
              <Label htmlFor="quiet-end">Quiet Hours End</Label>
              <Input
                id="quiet-end"
                type="time"
                value={preferences.quiet_hours_end}
                onChange={(e) => updatePreferences({ quiet_hours_end: e.target.value })}
                disabled={isSaving}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="timezone">Timezone</Label>
            <Input
              id="timezone"
              value={preferences.timezone}
              onChange={(e) => updatePreferences({ timezone: e.target.value })}
              disabled={isSaving}
              placeholder="UTC"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}